import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../LoopinoDesktopGuidelines.css';
import '../DesktopLogin/DesktopLogin.css';
import './DesktopPostReviewPage.css';
import useMobilePostReview from '../../../hooks/useMobilePostReview';
import DesktopPostInfo from './components/DesktopPostInfo';
import DesktopPostVersions from './components/DesktopPostVersions';
import { Iphone16Pro } from './components/Iphone16Pro';
import DesktopPostEditPage from './DesktopPostEditPage';
import AIRegenerationModal from '../../Mobile/MobilePostReview/AIRegenerationModal';
import TextEditModal from '../../Mobile/MobilePostReview/TextEditModal';
import DesktopImageGalleryPage from '../DesktopImageGallery/DesktopImageGalleryPage';
import { Skeleton } from '../../effects';
import { getAllMediaUrls, getFirstMediaUrl } from '../../../utils/mediaHelpers';
import { useAuthContext } from '../../../context/AuthContext';
import API_CONFIG from '../../../config/api';
import { SUPPORTED_MEDIA_ACCEPT, SUPPORTED_MEDIA_LABEL, isSupportedMediaFile } from '../../Mobile/mediaUploadConfig';
import campaignService from '../../../services/CampaignService';
import { DesktopAlertProvider } from '../common/alerts/DesktopAlertProvider';
import { useDesktopAlert } from '../common/alerts/DesktopAlertContext';
import { useCapabilities } from '../../../capabilities/CapabilityContext';
import useLanguage from '../../../hooks/useLanguage';
import { normalizeMediaUrl } from '../../../utils/normalizeMediaUrl';
import { apiFetch, parseResponseBody } from '../../../utils/http';
import useDesktopScrollLock, { forceUnlockScroll } from './useDesktopScrollLock';
import useCompany from '../../../hooks/useCompany';
import { hasGeneratedDna } from '../../../utils/companyDna';
import DesktopThreadsBackground from '../DesktopLogin/DesktopThreadsBackground';
import DesktopHorizontalScroll from '../common/scroll/DesktopHorizontalScroll';
import logger from '../../../utils/clientLogger';

const normalizePostIdValue = (value) => {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'object') {
    if (value.$oid) {
      return String(value.$oid);
    }

    if (value.id || value._id) {
      return normalizePostIdValue(value.id ?? value._id);
    }

    if (typeof value.toString === 'function') {
      const stringified = value.toString();
      if (stringified && stringified !== '[object Object]') {
        return String(stringified);
      }
    }
  }

  return null;
};

const getPostIdentifier = (postish) => {
  if (!postish) {
    return null;
  }

  if (typeof postish === 'string' || typeof postish === 'number') {
    return String(postish);
  }

  return (
    normalizePostIdValue(postish.id) ||
    normalizePostIdValue(postish._id) ||
    normalizePostIdValue(postish.post_id) ||
    normalizePostIdValue(postish.postId) ||
    null
  );
};

const getVersionIdentifier = (version) => {
  return (
    version?.version_id ||
    version?.id ||
    version?._id ||
    version?.version ||
    version?.version_number ||
    null
  );
};

const getVersionTimestamp = (version) => {
  const timestamp = version?.timestamp ||
    version?.created_at ||
    version?.createdAt ||
    version?.generated_at ||
    version?.generatedAt;

  if (!timestamp) return null;

  try {
    const value = typeof timestamp === 'object' && timestamp.$date
      ? timestamp.$date
      : timestamp;
    const time = new Date(value).getTime();
    return Number.isFinite(time) ? time : null;
  } catch (error) {
    logger.error('Failed to parse version timestamp', error, { kind: 'user' });
    return null;
  }
};

const sortVersionsDescending = (versions) => {
  if (!Array.isArray(versions)) return [];

  return [...versions].sort((a, b) => {
    const timeA = getVersionTimestamp(a);
    const timeB = getVersionTimestamp(b);

    if (timeA !== null && timeB !== null && timeA !== timeB) {
      return timeB - timeA;
    }

    if (timeA !== null && timeB === null) return -1;
    if (timeA === null && timeB !== null) return 1;

    const identifierA = getVersionIdentifier(a);
    const identifierB = getVersionIdentifier(b);

    if (identifierA !== null && identifierB !== null && identifierA !== identifierB) {
      if (typeof identifierA === 'number' && typeof identifierB === 'number') {
        return identifierB - identifierA;
      }
      return String(identifierB).localeCompare(String(identifierA));
    }

    return 0;
  });
};

const getPostScheduledTimestamp = (post) => {
  if (!post) return null;

  const rawValue =
    post?.scheduledDate ||
    post?.scheduled_time ||
    post?.scheduledTime ||
    post?.scheduled_at ||
    post?.scheduledAt;

  if (!rawValue) {
    return null;
  }

  try {
    const normalized = typeof rawValue === 'object' && rawValue.$date
      ? rawValue.$date
      : rawValue;
    const timestamp = new Date(normalized).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  } catch (error) {
    return null;
  }
};

const getTargetPostIdFromUrl = () => {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search || '');
    return params.get('postId') || params.get('targetPostId') || null;
  } catch (error) {
    logger.error('Unable to read target post id from URL', error, { kind: 'user' });
    return null;
  }
};

const generateMediaId = () => {
  return `desktop-media-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
};

const formatDeviceClock = () => {
  try {
    return new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit' }).format(new Date());
  } catch (error) {
    const date = new Date();
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }
};

const formatPostRelativeTimeLabel = (timestamp, t) => {
  const translate = typeof t === 'function'
    ? t
    : (_key, options) => options?.defaultValue || '';

  if (!timestamp) {
    return translate('postReview.preview.relativeTime.now', { defaultValue: 'adesso' });
  }

  const diffMs = Date.now() - timestamp;
  const inFuture = diffMs < 0;
  const absMinutes = Math.max(1, Math.round(Math.abs(diffMs) / 60000));

  if (absMinutes < 60) {
    const unit = translate('postReview.preview.relativeTime.units.minute', { defaultValue: 'm' });
    return inFuture
      ? translate('postReview.preview.relativeTime.future', { value: absMinutes, unit, defaultValue: 'tra {{value}}{{unit}}' })
      : translate('postReview.preview.relativeTime.past', { value: absMinutes, unit, defaultValue: '{{value}}{{unit}}' });
  }

  const absHours = Math.round(absMinutes / 60);
  if (absHours < 24) {
    const unit = translate('postReview.preview.relativeTime.units.hour', { defaultValue: 'h' });
    return inFuture
      ? translate('postReview.preview.relativeTime.future', { value: absHours, unit, defaultValue: 'tra {{value}}{{unit}}' })
      : translate('postReview.preview.relativeTime.past', { value: absHours, unit, defaultValue: '{{value}}{{unit}}' });
  }

  const absDays = Math.round(absHours / 24);
  const unit = translate('postReview.preview.relativeTime.units.day', { defaultValue: 'g' });
  return inFuture
    ? translate('postReview.preview.relativeTime.future', { value: absDays, unit, defaultValue: 'tra {{value}}{{unit}}' })
    : translate('postReview.preview.relativeTime.past', { value: absDays, unit, defaultValue: '{{value}}{{unit}}' });
};

const normalizeHashtagsList = (hashtags) => {
  if (Array.isArray(hashtags)) {
    return hashtags
      .map((tag) => (tag?.startsWith('#') ? tag : `#${tag}`))
      .filter(Boolean);
  }

  if (typeof hashtags === 'string') {
    return hashtags
      .split(/[\s,]+/)
      .map((tag) => tag.trim())
      .filter(Boolean)
      .map((tag) => (tag.startsWith('#') ? tag : `#${tag}`));
  }

  return [];
};

const isPostPublished = (post) => {
  if (!post) return false;

  const statusRaw = post?.status;
  let normalizedStatus = '';

  if (typeof statusRaw === 'string') {
    normalizedStatus = statusRaw.toLowerCase();
  } else if (statusRaw && typeof statusRaw === 'object') {
    const candidate = statusRaw.value || statusRaw.current || statusRaw.status || statusRaw.state || '';
    normalizedStatus = candidate ? candidate.toString().toLowerCase() : '';
  } else if (statusRaw !== undefined && statusRaw !== null) {
    normalizedStatus = String(statusRaw).toLowerCase();
  }

  const isExplicitlyPosted = post?.posted === true;
  return isExplicitlyPosted || normalizedStatus === 'published' || normalizedStatus === 'posted';
};

const getCampaignIdentifier = (postish) => {
  if (!postish) return null;
  return normalizePostIdValue(
    postish.campaignId ||
    postish.campaign_id ||
    postish.campaign?.id ||
    postish.campaign?._id ||
    null
  );
};

const BackIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CalendarIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="white" strokeWidth="2" />
    <line x1="16" y1="2" x2="16" y2="6" stroke="white" strokeWidth="2" />
    <line x1="8" y1="2" x2="8" y2="6" stroke="white" strokeWidth="2" />
    <line x1="3" y1="10" x2="21" y2="10" stroke="white" strokeWidth="2" />
  </svg>
);

const ClockIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const DesktopPostEditPortal = ({
  children,
  anchorKey,
  headerTitle,
  onRequestGallery,
  headerContent = null,
  footerContent = null
}) => {
  const wrapperRef = useRef(null);
  const [headerPortalNode, setHeaderPortalNode] = useState(null);
  const [footerPortalNode, setFooterPortalNode] = useState(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const cleanupClasses = [];
    const cleanupAttributes = [];

    const addClass = (node, className) => {
      if (node && className) {
        node.classList.add(className);
        cleanupClasses.push([node, className]);
      }
    };

    const setAttribute = (node, name, value) => {
      if (node && name) {
        node.setAttribute(name, value ?? '');
        cleanupAttributes.push([node, name]);
      }
    };

    if (!wrapper) {
      return () => {};
    }

    const pageNode = wrapper.querySelector('.post-edit-page');
    addClass(pageNode, 'loopino-desktop-editor-page');

    const contentNode = wrapper.querySelector('.post-edit-content');
    if (!contentNode) {
      return () => {
        cleanupClasses.forEach(([node, className]) => node?.classList.remove(className));
        cleanupAttributes.forEach(([node, attr]) => node?.removeAttribute(attr));
      };
    }

    addClass(contentNode, 'loopino-desktop-editor-content');

    const headerNode = contentNode.querySelector('.post-edit-header');
    addClass(headerNode, 'loopino-desktop-editor-header');

    const backButton = headerNode?.querySelector('.post-edit-back-button');
    addClass(backButton, 'loopino-desktop-editor-header-back');

    const spacerNode = headerNode?.querySelector('.post-edit-header-spacer');
    addClass(spacerNode, 'loopino-desktop-editor-header-spacer');

    const titleNode = headerNode?.querySelector('h3');
    if (titleNode) {
      addClass(titleNode, 'loopino-desktop-editor-header-title');
      const resolvedTitle = headerTitle || titleNode.textContent || '';
      titleNode.textContent = resolvedTitle;
      setAttribute(titleNode, 'data-desktop-title', resolvedTitle);
    }

    // Clone the drag ghost into <body> so it's not clipped inside desktop containers
    let ghostLinks = [];

    const linkGhostToBody = (ghost) => {
      if (
        !ghost ||
        ghost.dataset.loopinoDesktopGhostLinked === 'true' ||
        ghost.dataset.loopinoDesktopGhostManaged === 'true' ||
        ghost.dataset.loopinoDesktopGhostClone === 'true'
      ) {
        return;
      }

      const clone = ghost.cloneNode(true);
      clone.dataset.loopinoDesktopGhostClone = 'true';

      const syncClone = () => {
        if (!ghost.isConnected) {
          clone.remove();
          return;
        }
        // Mirror style and content while keeping it always on top
        clone.className = ghost.className;
        clone.style.cssText = ghost.getAttribute('style') || '';
        clone.style.position = 'fixed';
        clone.style.pointerEvents = 'none';
        clone.style.zIndex = '9999';
        clone.style.display = 'block';
        clone.innerHTML = ghost.innerHTML;
      };

      syncClone();
      const ghostObserver = new MutationObserver(syncClone);
      ghostObserver.observe(ghost, {
        attributes: true,
        attributeFilter: ['style', 'class'],
        childList: true,
        subtree: true,
        characterData: true
      });

      ghost.dataset.loopinoDesktopGhostLinked = 'true';
      document.body.appendChild(clone);
      ghostLinks.push({ ghost, clone, ghostObserver });
    };

    const ensureGhostClone = () => {
      // Clean up stale clones whose source ghost has been removed
      ghostLinks = ghostLinks.filter(({ ghost, clone, ghostObserver }) => {
        if (ghost?.isConnected) return true;
        ghostObserver?.disconnect();
        clone?.remove();
        return false;
      });

      const ghost = Array.from(document.querySelectorAll('.post-edit-drag-ghost'))
        .find((node) =>
          node.dataset.loopinoDesktopGhostClone !== 'true' &&
          node.dataset.loopinoDesktopGhostManaged !== 'true'
        );
      if (ghost) {
        linkGhostToBody(ghost);
      }
    };

    ensureGhostClone();
    const observer = new MutationObserver(ensureGhostClone);
    observer.observe(document.body, { childList: true, subtree: true });
    const observers = [observer];

    const cleanupListeners = [];

    const bindGalleryButton = () => {
      const galleryButtons = contentNode.querySelectorAll('.post-edit-add-image-buttons .post-edit-add-image-main-btn');
      const galleryButton = galleryButtons?.[2] || null;
      if (!galleryButton || galleryButton.dataset.loopinoDesktopGalleryBound === 'true') {
        return;
      }

      const handleGalleryClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
        if (typeof onRequestGallery === 'function') {
          onRequestGallery();
        }
      };

      galleryButton.addEventListener('click', handleGalleryClick, true);
      galleryButton.dataset.loopinoDesktopGalleryBound = 'true';
      cleanupListeners.push(() => {
        galleryButton.removeEventListener('click', handleGalleryClick, true);
        delete galleryButton.dataset.loopinoDesktopGalleryBound;
      });
    };

    bindGalleryButton();

    const buttonsObserver = new MutationObserver(() => {
      bindGalleryButton();
    });

    buttonsObserver.observe(contentNode, { childList: true, subtree: true });
    cleanupListeners.push(() => buttonsObserver.disconnect());

    let injectedHeader = null;
    let injectedFooter = null;

    if (contentNode) {
      injectedHeader = document.createElement('div');
      injectedHeader.className = 'loopino-desktop-editor-injected-header';
      if (headerNode && headerNode.parentNode) {
        headerNode.insertAdjacentElement('afterend', injectedHeader);
      } else {
        contentNode.insertBefore(injectedHeader, contentNode.firstChild);
      }

      injectedFooter = document.createElement('div');
      injectedFooter.className = 'loopino-desktop-editor-injected-footer';
      contentNode.appendChild(injectedFooter);

      setHeaderPortalNode(injectedHeader);
      setFooterPortalNode(injectedFooter);
    }

    return () => {
      setHeaderPortalNode(null);
      setFooterPortalNode(null);
      injectedHeader?.remove();
      injectedFooter?.remove();
      cleanupClasses.forEach(([node, className]) => node?.classList.remove(className));
      cleanupAttributes.forEach(([node, attr]) => node?.removeAttribute(attr));
      cleanupListeners.forEach((fn) => fn());
      observers.forEach((obs) => obs.disconnect());
      ghostLinks.forEach(({ clone, ghostObserver }) => {
        ghostObserver.disconnect();
        clone?.remove();
      });
      document.querySelectorAll('.post-edit-drag-ghost[data-loopino-desktop-ghost-linked]').forEach((ghost) => {
        ghost.removeAttribute('data-loopino-desktop-ghost-linked');
      });
      document.querySelectorAll('[data-loopino-desktop-ghost-clone]').forEach((clone) => {
        if (clone.dataset.loopinoDesktopGhostManaged === 'true') return;
        clone.remove();
      });
    };
  }, [anchorKey, headerTitle, onRequestGallery]);

  return (
    <div className="loopino-desktop-editor-wrapper" ref={wrapperRef}>
      {children}
      {headerPortalNode && headerContent ? createPortal(headerContent, headerPortalNode) : null}
      {footerPortalNode && footerContent ? createPortal(footerContent, footerPortalNode) : null}
    </div>
  );
};

const DesktopPostReviewPageInner = ({ onNavigate = null, onReady = null }) => {
  const { t } = useLanguage();
  const { getNavigationStateForHref, isAccessible } = useCapabilities();
  const { logout: authLogout } = useAuthContext();
  const heroThreadsColor = useMemo(() => [0.98, 0.92, 1], []);
  const {
    posts: apiPosts,
    isLoading: apiIsLoading,
    isRegenerating: apiIsRegenerating,
    error: apiError,
    approvePost: apiApprovePost,
    rejectPost: apiRejectPost,
    regeneratePost: apiRegeneratePost,
    editPostText: apiEditPostText,
    handleImageUpload: apiHandleImageUpload,
    deletePost: apiDeletePost,
    refresh: apiRefresh,
    clearError: apiClearError
  } = useMobilePostReview();

  const { notify } = useDesktopAlert();
  const { currentCompany, companies } = useCompany();
  const isBrowser = typeof window !== 'undefined';
  const hasDocument = typeof document !== 'undefined';

  const [posts, setPosts] = useState([]);
  const [currentPostIndex, setCurrentPostIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [postVersions, setPostVersions] = useState({});
  const [currentVersionIndices, setCurrentVersionIndices] = useState({});
  const [definitiveVersionIndices, setDefinitiveVersionIndices] = useState({});
  const [isSettingDefinitiveVersion, setIsSettingDefinitiveVersion] = useState(false);
  const [postCampaignNames, setPostCampaignNames] = useState({});
  const campaignNameCacheRef = useRef(new Map());

  const [aiModalOpen, setAIModalOpen] = useState(false);
  const [aiModalType, setAIModalType] = useState('full');
  const [aiModalImageIndex, setAiModalImageIndex] = useState(null);
  const [textEditModalOpen, setTextEditModalOpen] = useState(false);
  const [isTextSaving, setIsTextSaving] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAiRegeneratingUi, setIsAiRegeneratingUi] = useState(false);
  const targetPostId = useMemo(() => getTargetPostIdFromUrl(), []);
  const hasInitializedRef = useRef(false);
  const postChipRefs = useRef([]);
  const deviceTime = useMemo(() => formatDeviceClock(), []);
  const [imageLoadingStates, setImageLoadingStates] = useState(() => new Map());
  const [isDesktopGalleryOpen, setDesktopGalleryOpen] = useState(false);
  const editorPanelRef = useRef(null);
  const postReviewGridRef = useRef(null);
  const previewPanelRef = useRef(null);
  const previewPlaceholderRef = useRef(null);
  const pageHeaderRef = useRef(null);
  const [previewStickyTop, setPreviewStickyTop] = useState(() => 36);
  const previewPanelStyle = useMemo(() => {
    if (!Number.isFinite(previewStickyTop)) return undefined;
    return { '--loopino-desktop-preview-sticky-top': `${previewStickyTop}px` };
  }, [previewStickyTop]);
  const [previewDynamicStyle, setPreviewDynamicStyle] = useState({});
  const [previewPinMode, setPreviewPinMode] = useState('static'); // static | fixed | bottom
  const previewRafRef = useRef(null);
  const readyAnnouncedRef = useRef(false);
  const hasInitialLoadStartedRef = useRef(false);
  const [hasInitialLoadCompleted, setHasInitialLoadCompleted] = useState(false);
  const isLoading = apiIsLoading;
  const isRegenerating = apiIsRegenerating || isAiRegeneratingUi;
  const isUiLoading = isLoading || !hasInitialLoadCompleted;
  useEffect(() => {
    if (isLoading) {
      hasInitialLoadStartedRef.current = true;
      return;
    }

    if (hasInitialLoadStartedRef.current && !hasInitialLoadCompleted) {
      setHasInitialLoadCompleted(true);
    }
  }, [isLoading, hasInitialLoadCompleted]);
  useEffect(() => {
    if (readyAnnouncedRef.current || !hasInitialLoadCompleted) {
      return;
    }
    readyAnnouncedRef.current = true;
    if (typeof onReady === 'function') {
      onReady();
    }
  }, [hasInitialLoadCompleted, onReady]);
  const [editorModalOffsets, setEditorModalOffsets] = useState({
    top: 0,
    left: 0,
    right: 0,
    bottom: 0
  });
  const currentPost = posts[currentPostIndex] || null;
  const currentPostId = useMemo(() => getPostIdentifier(currentPost), [currentPost]);
  const isCurrentPostPublished = useMemo(() => isPostPublished(currentPost), [currentPost]);
  const isEditorLocked = isCurrentPostPublished || isRegenerating;
  const [isDesktopDatePickerOpen, setDesktopDatePickerOpen] = useState(false);
  const [isDesktopTimePickerOpen, setDesktopTimePickerOpen] = useState(false);
  const [desktopSelectedDate, setDesktopSelectedDate] = useState(() => new Date());
  const [desktopSelectedTime, setDesktopSelectedTime] = useState('12:00');
  const [desktopPickerMonth, setDesktopPickerMonth] = useState(() => new Date());
  const [hasAnyCampaigns, setHasAnyCampaigns] = useState(false);
  const [isCampaignStateReady, setIsCampaignStateReady] = useState(false);
  const desktopDatePickerRef = useRef(null);
  const desktopTimeInputRef = useRef(null);
  const desktopTimePanelRef = useRef(null);
  const shouldLockScroll = aiModalOpen ||
    textEditModalOpen ||
    isDesktopDatePickerOpen ||
    isDesktopTimePickerOpen ||
    isDesktopGalleryOpen;
  const isBlockingModalActive = shouldLockScroll;
  useDesktopScrollLock(shouldLockScroll, 'desktop-post-review');
  useEffect(() => {
    return () => {
      forceUnlockScroll();
    };
  }, []);
  useEffect(() => {
    if (!shouldLockScroll) {
      forceUnlockScroll();
    }
  }, [shouldLockScroll]);
  const formatTimeForInput = useCallback((dateValue) => {
    if (!dateValue) return '12:00';
    try {
      const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
      if (isNaN(date.getTime())) return '12:00';
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (error) {
      logger.error('Error formatting time for input', error, { kind: 'user' });
      return '12:00';
    }
  }, []);
  const isSameDay = useCallback((dateA, dateB) => {
    if (!dateA || !dateB) return false;
    return (
      dateA.getFullYear() === dateB.getFullYear() &&
      dateA.getMonth() === dateB.getMonth() &&
      dateA.getDate() === dateB.getDate()
    );
  }, []);
  const combineDateAndTimeDesktop = useCallback((dateValue, timeValue) => {
    if (isEditorLocked) return null;
    if (!dateValue || !timeValue) return null;
    const [hours, minutes] = timeValue.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }

    const combinedDate = new Date(dateValue);
    combinedDate.setHours(hours, minutes, 0, 0);

    if (isNaN(combinedDate.getTime())) {
      return null;
    }

    setPosts(prev => prev.map((post, index) => {
      if (index !== currentPostIndex) {
        return post;
      }
      return {
        ...post,
        scheduled_time: combinedDate.toISOString(),
        scheduledDate: combinedDate.toISOString(),
        __hasPendingRegeneratedChanges: true
      };
    }));

    return combinedDate;
  }, [currentPostIndex, isEditorLocked]);
  const desktopToday = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);
  const desktopWeekdayLabels = useMemo(() => ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'], []);
  const desktopCalendarDays = useMemo(() => {
    const start = new Date(desktopPickerMonth.getFullYear(), desktopPickerMonth.getMonth(), 1);
    const leadingEmpty = (start.getDay() + 6) % 7;
    const daysInMonth = new Date(desktopPickerMonth.getFullYear(), desktopPickerMonth.getMonth() + 1, 0).getDate();
    const days = [];

    for (let i = 0; i < leadingEmpty; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(desktopPickerMonth.getFullYear(), desktopPickerMonth.getMonth(), day));
    }

    while (days.length % 7 !== 0) {
      days.push(null);
    }

    return days;
  }, [desktopPickerMonth]);
  const desktopMonthLabel = useMemo(
    () => desktopPickerMonth.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' }),
    [desktopPickerMonth]
  );
  const desktopHourOptions = useMemo(
    () => Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')),
    []
  );
  const desktopMinuteOptions = useMemo(
    () => Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')),
    []
  );
  const syncDatePickerWithPost = useCallback((postOverride = null) => {
    const targetPost = postOverride || currentPost;
    const timestamp = getPostScheduledTimestamp(targetPost) ?? Date.now();
    const baseDate = new Date(timestamp);
    const safeDate = isNaN(baseDate.getTime()) ? new Date() : baseDate;
    setDesktopSelectedDate(safeDate);
    setDesktopPickerMonth(safeDate);
    setDesktopSelectedTime(formatTimeForInput(safeDate));
  }, [currentPost, formatTimeForInput]);
  useEffect(() => {
    syncDatePickerWithPost();
  }, [syncDatePickerWithPost]);
  const handleDesktopDateSelect = useCallback((newDate) => {
    if (isEditorLocked) return;
    if (!newDate) return;
    const normalized = new Date(newDate);
    normalized.setHours(0, 0, 0, 0);
    if (normalized < desktopToday) {
      return;
    }
    setDesktopSelectedDate(normalized);
    setDesktopPickerMonth(normalized);
    const timeToUse = desktopSelectedTime || formatTimeForInput(currentPost?.scheduled_time || currentPost?.scheduledDate || null);
    const resolvedTime = timeToUse || '12:00';
    setDesktopSelectedTime(resolvedTime);
    combineDateAndTimeDesktop(normalized, resolvedTime);
  }, [combineDateAndTimeDesktop, currentPost, desktopSelectedTime, desktopToday, formatTimeForInput, isEditorLocked]);
  const handleDesktopTimeInputChange = useCallback((timeValue) => {
    if (isEditorLocked) return;
    if (!timeValue) return;
    const finalTime = timeValue.slice(0, 5);
    setDesktopSelectedTime(finalTime);
    const dateToUse = desktopSelectedDate || new Date();
    combineDateAndTimeDesktop(dateToUse, finalTime);
    setDesktopTimePickerOpen(false);
  }, [combineDateAndTimeDesktop, desktopSelectedDate, isEditorLocked]);
  const handleDesktopHourSelect = useCallback((hour) => {
    if (isEditorLocked) return;
    const [, minutes = '00'] = (desktopSelectedTime || '12:00').split(':');
    handleDesktopTimeInputChange(`${hour}:${minutes}`);
  }, [desktopSelectedTime, handleDesktopTimeInputChange, isEditorLocked]);
  const handleDesktopMinuteSelect = useCallback((minute) => {
    if (isEditorLocked) return;
    const [hours = '12'] = (desktopSelectedTime || '12:00').split(':');
    handleDesktopTimeInputChange(`${hours}:${minute}`);
  }, [desktopSelectedTime, handleDesktopTimeInputChange, isEditorLocked]);
  const goToPreviousDesktopMonth = useCallback(() => {
    if (isEditorLocked) return;
    setDesktopPickerMonth(prev => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() - 1);
      return next;
    });
  }, [isEditorLocked]);
  const goToNextDesktopMonth = useCallback(() => {
    if (isEditorLocked) return;
    setDesktopPickerMonth(prev => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() + 1);
      return next;
    });
  }, [isEditorLocked]);
  const openDesktopTimePicker = useCallback(() => {
    if (isEditorLocked) return;
    const el = desktopTimeInputRef.current;
    if (el) {
      el.focus();
    }
    setDesktopTimePickerOpen(true);
  }, [isEditorLocked]);
  useEffect(() => {
    if (!isDesktopTimePickerOpen) return undefined;
    const handleClickOutside = (event) => {
      if (desktopTimePanelRef.current?.contains(event.target) || desktopTimeInputRef.current?.contains(event.target)) {
        return;
      }
      setDesktopTimePickerOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isDesktopTimePickerOpen]);
  useEffect(() => {
    if (!isDesktopDatePickerOpen) return undefined;
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setDesktopDatePickerOpen(false);
        setDesktopTimePickerOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isDesktopDatePickerOpen]);
  const setImageLoadingState = useCallback((key, status) => {
    if (!key) return;
    setImageLoadingStates(prev => {
      const current = prev.get(key);
      if (current === status) {
        return prev;
      }
      const next = new Map(prev);
      next.set(key, status);
      return next;
    });
  }, [setImageLoadingStates]);

  const getImageLoadingProps = useCallback((source, scope = 'desktop') => {
    const normalizedScope = scope || 'desktop';
    const baseKey = typeof source === 'string' && source.length > 0 ? source : 'unknown';
    const mapKey = `${normalizedScope}::${baseKey}`;
    const status = imageLoadingStates.get(mapKey) || 'loading';
    const statusClass = status === 'loaded'
      ? 'is-ready'
      : status === 'error'
        ? 'is-error'
        : 'is-loading';

    return {
      status,
      className: statusClass,
      onLoad: () => setImageLoadingState(mapKey, 'loaded'),
      onError: () => setImageLoadingState(mapKey, 'error')
    };
  }, [imageLoadingStates, setImageLoadingState]);

  const toAbsoluteMediaUrl = useCallback((url) => {
    if (!url) return null;
    try {
      const resolved = new URL(url, API_CONFIG.BASE_URL);
      return resolved.href;
    } catch (error) {
      return url;
    }
  }, []);

  const resolveGallerySelectionUrl = useCallback((item) => {
    if (!item) return null;
    const candidate = item.selectedUrl || item.url || item.displayUrl || item.raw?.public_url || item.raw?.url || item.raw?.file_url;
    if (!candidate) {
      return null;
    }
    return toAbsoluteMediaUrl(candidate);
  }, [toAbsoluteMediaUrl]);

  const mergeSelectedUrlsIntoPost = useCallback((post, urls) => {
    if (!post || !Array.isArray(urls) || urls.length === 0) {
      return post;
    }

    const existingMediaUrls = Array.isArray(post.media_urls)
      ? post.media_urls
      : Array.isArray(post.images)
        ? post.images
        : [];

    const mergedUrls = Array.from(new Set([...existingMediaUrls, ...urls]));

    const existingMediaArray = Array.isArray(post.mediaArray) ? [...post.mediaArray] : [];
    const knownUrls = new Set(existingMediaArray.map((item) => item?.url || item?.previewUrl).filter(Boolean));
    const newMediaItems = mergedUrls
      .filter((url) => !knownUrls.has(url))
      .map((url) => ({
        id: generateMediaId(),
        type: 'url',
        url,
        previewUrl: url
      }));

    return {
      ...post,
      media_urls: mergedUrls,
      images: mergedUrls,
      mediaArray: [...existingMediaArray, ...newMediaItems],
      __hasPendingRegeneratedChanges: true
    };
  }, []);

  const handleDesktopGallerySelect = useCallback((selection) => {
    if (isEditorLocked) {
      setDesktopGalleryOpen(false);
      return;
    }
    const items = Array.isArray(selection) ? selection : [selection];
    const selectedUrls = items
      .map(resolveGallerySelectionUrl)
      .filter(Boolean);

    if (!selectedUrls.length) {
      setDesktopGalleryOpen(false);
      return;
    }

    let nextMediaLength = null;
    setPosts((prevPosts) => prevPosts.map((post, index) => {
      if (index !== currentPostIndex) {
        return post;
      }

      const updatedPost = mergeSelectedUrlsIntoPost(post, selectedUrls);
      nextMediaLength = Array.isArray(updatedPost.media_urls)
        ? updatedPost.media_urls.length
        : Array.isArray(updatedPost.images)
          ? updatedPost.images.length
          : null;

      return updatedPost;
    }));

    if (nextMediaLength !== null) {
      setCurrentImageIndex(Math.max(0, nextMediaLength - 1));
    }

    setDesktopGalleryOpen(false);
  }, [currentPostIndex, isEditorLocked, mergeSelectedUrlsIntoPost, resolveGallerySelectionUrl]);

  const handleOpenDesktopGallery = useCallback(() => {
    if (isEditorLocked) return;
    setDesktopGalleryOpen(true);
  }, [isEditorLocked]);

  const handleCloseDesktopGallery = useCallback(() => {
    setDesktopGalleryOpen(false);
  }, []);

  const applyPreviewLayout = useCallback((nextMode, nextStyle, placeholderHeight) => {
    setPreviewPinMode(prevMode => (prevMode === nextMode ? prevMode : nextMode));
    setPreviewDynamicStyle(prevStyle => {
      const keys = ['position', 'top', 'left', 'width', 'height'];
      const isSame = keys.every(key => prevStyle?.[key] === nextStyle?.[key]);
      return isSame ? prevStyle : nextStyle;
    });

    const placeholderNode = previewPlaceholderRef.current;
    if (placeholderNode && Number.isFinite(placeholderHeight)) {
      placeholderNode.style.height = `${placeholderHeight}px`;
    } else if (placeholderNode) {
      placeholderNode.style.height = '0px';
    }
  }, []);

  const recomputePreviewPosition = useCallback(() => {
    const previewNode = previewPanelRef.current;
    const gridNode = postReviewGridRef.current;
    if (!previewNode || !gridNode) {
      applyPreviewLayout('static', { position: 'relative', width: '100%', height: 'auto' }, 0);
      return;
    }

    const stickyTop = Number.isFinite(previewStickyTop) ? previewStickyTop : 32;
    if (typeof window !== 'undefined' && window.innerWidth <= 1100) {
      applyPreviewLayout('static', { position: 'relative', width: '100%', height: 'auto' }, 0);
      return;
    }
    const phoneHeight = previewNode.offsetHeight || 0;
    const phoneWidth = previewNode.offsetWidth || gridNode.clientWidth || 360;
    if (!phoneHeight) {
      applyPreviewLayout('static', { position: 'relative' }, 0);
      return;
    }

    const containerRect = gridNode.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const scrollX = window.scrollX || window.pageXOffset || 0;
    const containerTop = containerRect.top + scrollY;
    const containerBottom = containerRect.bottom + scrollY;

    const startStick = containerTop - stickyTop;
    const endStick = containerBottom - stickyTop - phoneHeight;

    if (scrollY >= endStick) {
      // reached bottom of the container
      applyPreviewLayout('bottom', {
        position: 'absolute',
        top: `${Math.max(0, containerRect.height - phoneHeight)}px`,
        left: '0px',
        width: `${phoneWidth}px`,
        height: 'auto'
      }, phoneHeight);
      return;
    }

    if (scrollY >= startStick) {
      // stick to viewport
      applyPreviewLayout('fixed', {
        position: 'fixed',
        top: `${stickyTop}px`,
        left: `${containerRect.left + scrollX}px`,
        width: `${phoneWidth}px`,
        height: 'auto'
      }, phoneHeight);
      return;
    }

    applyPreviewLayout('static', {
      position: 'relative',
      top: 'auto',
      left: 'auto',
      width: '100%',
      height: 'auto'
    }, 0);
  }, [applyPreviewLayout, postReviewGridRef, previewStickyTop]);

  const schedulePreviewUpdate = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (previewRafRef.current) {
      return;
    }
    previewRafRef.current = window.requestAnimationFrame(() => {
      previewRafRef.current = null;
      recomputePreviewPosition();
    });
  }, [recomputePreviewPosition]);

  useEffect(() => {
    const updatePreviewStickyTop = () => {
      if (typeof window === 'undefined') {
        return;
      }

      try {
        // Anchor the sticky device below the desktop shell padding/safe area.
        const offsets = [32];
        const headerNode = pageHeaderRef.current;
        if (headerNode instanceof HTMLElement) {
          const headerRect = headerNode.getBoundingClientRect();
          if (Number.isFinite(headerRect.bottom)) {
            offsets.push(headerRect.bottom + 12);
          }
        }

        const shellNode = document.querySelector('.loopino-desktop-post-review-shell');
        if (shellNode instanceof HTMLElement) {
          const styles = window.getComputedStyle(shellNode);
          const paddingTop = parseFloat(styles.paddingTop || '0');
          if (!Number.isNaN(paddingTop)) {
            offsets.push(paddingTop - 12);
          }
        }

        const offset = Math.max(20, Math.min(160, Math.max(...offsets)));
        if (Number.isFinite(offset)) {
          setPreviewStickyTop(prev => (prev === offset ? prev : offset));
        }
      } catch (error) {
        logger.error('Unable to compute sticky offset', error, { kind: 'user' });
        setPreviewStickyTop(prev => (Number.isFinite(prev) ? prev : 36));
      }
    };

    updatePreviewStickyTop();
    window.addEventListener('resize', updatePreviewStickyTop);
    return () => {
      window.removeEventListener('resize', updatePreviewStickyTop);
    };
  }, []);

  useEffect(() => {
    recomputePreviewPosition();
  }, [recomputePreviewPosition, currentPostId, posts.length, previewStickyTop]);

  useEffect(() => {
    const handleScroll = () => schedulePreviewUpdate();
    const handleResize = () => schedulePreviewUpdate();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (previewRafRef.current) {
        window.cancelAnimationFrame(previewRafRef.current);
        previewRafRef.current = null;
      }
    };
  }, [schedulePreviewUpdate]);

  useEffect(() => {
    schedulePreviewUpdate();
  }, [schedulePreviewUpdate, isLoading, currentImageIndex, currentPostId, posts.length, previewStickyTop]);

  const updateEditorModalOffsets = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const panel = editorPanelRef.current;
    if (!panel) {
      return;
    }

    const rect = panel.getBoundingClientRect();
    setEditorModalOffsets({
      top: rect.top,
      left: rect.left,
      right: Math.max(0, window.innerWidth - rect.right),
      bottom: Math.max(0, window.innerHeight - rect.bottom)
    });
  }, []);

  const unsupportedFormatMessage = useMemo(
    () => t('postReview.postEdit.media.unsupportedFormat', { defaultValue: `Formato non supportato. Usa: ${SUPPORTED_MEDIA_LABEL}` }),
    [t]
  );

  useEffect(() => {
    const sortedPosts = (apiPosts || []).sort((a, b) => {
      const timeA = getPostScheduledTimestamp(a) ?? 0;
      const timeB = getPostScheduledTimestamp(b) ?? 0;
      return timeA - timeB;
    });
    setPosts(sortedPosts);

    if (!sortedPosts.length) {
      hasInitializedRef.current = false;
      return;
    }

    const normalizedTargetId = getPostIdentifier(targetPostId);
    if (hasInitializedRef.current && !normalizedTargetId) {
      return;
    }

    const now = Date.now();
    const nextUpcomingIdx = sortedPosts.findIndex(post => {
      const timestamp = getPostScheduledTimestamp(post);
      return typeof timestamp === 'number' && timestamp > now;
    });

    let initialIndex = 0;
    if (normalizedTargetId) {
      const targetIndex = sortedPosts.findIndex(post => getPostIdentifier(post) === normalizedTargetId);
      if (targetIndex >= 0) {
        initialIndex = targetIndex;
      } else if (nextUpcomingIdx >= 0) {
        initialIndex = nextUpcomingIdx;
      } else {
        initialIndex = sortedPosts.length - 1;
      }
    } else {
      initialIndex = nextUpcomingIdx >= 0 ? nextUpcomingIdx : sortedPosts.length - 1;
    }

    setCurrentPostIndex(initialIndex);
    setCurrentImageIndex(0);
    hasInitializedRef.current = true;
  }, [apiPosts, targetPostId]);

  useEffect(() => {
    if (currentPostIndex >= posts.length) {
      setCurrentPostIndex(Math.max(0, posts.length - 1));
    }
  }, [posts.length, currentPostIndex]);

  useEffect(() => {
    const chip = postChipRefs.current?.[currentPostIndex];
    if (chip && typeof chip.scrollIntoView === 'function') {
      requestAnimationFrame(() => {
        chip.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      });
    }
  }, [currentPostIndex, posts.length]);

  useEffect(() => {
    if (!posts || posts.length === 0) {
      setPostVersions({});
      setCurrentVersionIndices({});
      setDefinitiveVersionIndices({});
      return;
    }

    const mappedVersions = {};
    posts.forEach(post => {
      const postId = getPostIdentifier(post);
      if (!postId) return;
      const apiVersions = post.versions || [];
      if (apiVersions.length > 0) {
        mappedVersions[postId] = sortVersionsDescending(apiVersions);
      }
    });
    setPostVersions(mappedVersions);

    setCurrentVersionIndices(prev => {
      const next = {};
      posts.forEach(post => {
        const postId = getPostIdentifier(post);
        if (!postId) return;
        const versionsForPost = mappedVersions[postId];
        if (!versionsForPost || versionsForPost.length === 0) {
          return;
        }

        let indexToUse = prev[postId];
        if (typeof indexToUse !== 'number' || indexToUse < 0 || indexToUse >= versionsForPost.length) {
          const currentVersionId = post.current_version_id;
          if (currentVersionId) {
            const apiIndex = versionsForPost.findIndex(v => getVersionIdentifier(v) === currentVersionId);
            if (apiIndex >= 0) {
              indexToUse = apiIndex;
            }
          }
        }

        if (typeof indexToUse !== 'number' || indexToUse < 0 || indexToUse >= versionsForPost.length) {
          indexToUse = 0;
        }

        next[postId] = indexToUse;
      });
      return next;
    });

    setDefinitiveVersionIndices(() => {
      const next = {};
      posts.forEach(post => {
        const postId = getPostIdentifier(post);
        if (!postId) return;
        const versionsForPost = mappedVersions[postId];
        if (!versionsForPost || versionsForPost.length === 0) {
          return;
        }

        const currentVersionId = post.current_version_id;
        if (currentVersionId) {
          const definitiveIndex = versionsForPost.findIndex(v => getVersionIdentifier(v) === currentVersionId);
          if (definitiveIndex >= 0) {
            next[postId] = definitiveIndex;
          }
        }
      });
      return next;
    });
  }, [posts]);

  useEffect(() => {
    const fetchCampaignNames = async () => {
      try {
        (posts || []).forEach(p => {
          const id = p?.campaignId || p?.campaign_id;
          const normalizedId = normalizePostIdValue(id);
          const name = p?.campaignName || p?.campaign_name || null;
          if (normalizedId && name && !campaignNameCacheRef.current.has(normalizedId)) {
            campaignNameCacheRef.current.set(normalizedId, name);
          }
        });

        const ids = Array.from(new Set((posts || [])
          .map(p => normalizePostIdValue(p?.campaignId || p?.campaign_id))
          .filter(Boolean)));

        const missing = ids.filter(id => !campaignNameCacheRef.current.has(id));
        if (missing.length > 0) {
          const results = await Promise.all(missing.map(async (id) => {
            try {
              const c = await campaignService.getCampaign(id);
              return { id, name: c?.name || null };
            } catch (error) {
              logger.network('Failed to fetch campaign name', error, { campaignId: id });
              return { id, name: null };
            }
          }));

          results.forEach(({ id, name }) => {
            if (id && name) {
              campaignNameCacheRef.current.set(id, name);
            }
          });
        }

        const snapshot = {};
        campaignNameCacheRef.current.forEach((value, key) => {
          snapshot[key] = value;
        });
        setPostCampaignNames(snapshot);
      } catch (error) {
        logger.network('Error resolving campaign names', error);
      }
    };

    if (posts && posts.length > 0) {
      fetchCampaignNames();
    }
  }, [posts]);
  const pageSummary = useMemo(() => {
    if (posts.length === 0) {
      return t('postReview.desktop.emptySummary', { defaultValue: 'Nessun post disponibile' });
    }
    return t('postReview.desktop.pagination', {
      defaultValue: 'Post {{current}} di {{total}}',
      current: currentPostIndex + 1,
      total: posts.length
    });
  }, [posts.length, currentPostIndex, t]);

  const resolveCampaignNameForPost = useCallback((post) => {
    const existing = post?.campaignName || post?.campaign_name;
    if (existing) return existing;
    const cId = normalizePostIdValue(post?.campaignId || post?.campaign_id);
    if (!cId) return null;
    return postCampaignNames[cId] || null;
  }, [postCampaignNames]);

  const resolvedCompanyData = useMemo(() => {
    if (currentCompany?.company) return currentCompany.company;
    if (currentCompany) return currentCompany;
    if (Array.isArray(companies) && companies.length > 0) return companies[0];
    return null;
  }, [companies, currentCompany]);
  const hasDNA = useMemo(() => {
    if (!resolvedCompanyData) {
      return false;
    }
    return hasGeneratedDna(resolvedCompanyData);
  }, [resolvedCompanyData]);
  const isCheckingCampaignState = hasDNA && posts.length === 0 && !isCampaignStateReady;
  const isEmptyStateReady = !isUiLoading && posts.length === 0 && isCampaignStateReady;

  const isValidCompanyImage = useCallback((url) => {
    if (!url || typeof url !== 'string') return false;
    if (url.startsWith('data:')) return false;
    if (url.startsWith('blob:')) return false;
    if (url.toLowerCase().includes('placeholder')) return false;
    return true;
  }, []);

  const resolveCompanyLogoUrl = useCallback((data) => {
    if (!data) return null;
    const companyInfo = data.company || data;
    const pickUrl = (entry) => {
      if (!entry) return null;
      if (typeof entry === 'string') return isValidCompanyImage(entry) ? entry : null;
      if (typeof entry === 'object') {
        if (isValidCompanyImage(entry.url)) return entry.url;
        if (isValidCompanyImage(entry.preview)) return entry.preview;
      }
      return null;
    };

    const { logo } = companyInfo;

    if (Array.isArray(logo) && logo.length > 0) {
      const latest = logo[logo.length - 1];
      const logoUrl = pickUrl(latest);
      if (logoUrl) return logoUrl;
    }

    const directLogo = pickUrl(logo);
    if (directLogo) return directLogo;

    if (Array.isArray(companyInfo.brand_images) && companyInfo.brand_images.length > 0) {
      const firstImage = companyInfo.brand_images[0];
      const imageUrl = pickUrl(firstImage);
      if (imageUrl) return imageUrl;
    }

    return null;
  }, [isValidCompanyImage]);

  const getStoredCompanyLogo = useCallback(() => {
    if (!isBrowser) return null;
    const storedPreview = window.localStorage.getItem('companyLogoPreview');
    const normalized = normalizeMediaUrl(storedPreview, API_CONFIG.BASE_URL);
    if (isValidCompanyImage(normalized)) {
      return normalized;
    }
    return null;
  }, [isBrowser, isValidCompanyImage]);

  const getDomCompanyLogo = useCallback(() => {
    if (!hasDocument) return null;
    const logoImg = document.querySelector('.mobile-company-logo-img');
    const logoSrc = normalizeMediaUrl(logoImg?.src, API_CONFIG.BASE_URL);
    if (isValidCompanyImage(logoSrc)) {
      return logoSrc;
    }
    return null;
  }, [hasDocument, isValidCompanyImage]);

  const companyAvatarUrl = useMemo(() => {
    const directLogo = normalizeMediaUrl(resolveCompanyLogoUrl(resolvedCompanyData), API_CONFIG.BASE_URL);
    if (directLogo) return directLogo;

    const domLogo = getDomCompanyLogo();
    if (domLogo) return domLogo;

    const storedLogo = getStoredCompanyLogo();
    if (storedLogo) return storedLogo;

    return null;
  }, [getDomCompanyLogo, getStoredCompanyLogo, resolveCompanyLogoUrl, resolvedCompanyData]);

  const companyProfileName = useMemo(() => {
    if (!resolvedCompanyData) return null;
    return resolvedCompanyData.name || resolvedCompanyData.company_name || null;
  }, [resolvedCompanyData]);
  const resolvedCompanyId = useMemo(() => {
    if (!resolvedCompanyData) return null;
    return resolvedCompanyData.id || resolvedCompanyData._id || resolvedCompanyData.company_id || null;
  }, [resolvedCompanyData]);

  const buildStrategizerDnaPath = useCallback((companyId) => {
    const params = new URLSearchParams();
    params.set('view', 'dna');
    if (companyId) {
      params.set('companyId', companyId);
    }
    return `/strategizer?${params.toString()}`;
  }, []);

  const navigateDesktop = useCallback((href) => {
    if (!href) return;
    if (typeof onNavigate === 'function') {
      onNavigate(href);
      return;
    }
    if (typeof window !== 'undefined') {
      window.history.pushState({}, '', href);
      window.dispatchEvent(new Event('popstate'));
    }
  }, [onNavigate]);

  const handleNavigateToDna = useCallback(() => {
    navigateDesktop(buildStrategizerDnaPath(resolvedCompanyId));
  }, [buildStrategizerDnaPath, navigateDesktop, resolvedCompanyId]);

  const handleNavigateToStrategy = useCallback(() => {
    navigateDesktop('/desktop/new-strategy');
  }, [navigateDesktop]);

  const handleNavigateToCreatePost = useCallback(() => {
    const capability = getNavigationStateForHref('/desktop/create-post');
    if (capability.managed && !isAccessible(capability)) {
      return;
    }
    navigateDesktop('/desktop/create-post');
  }, [getNavigationStateForHref, isAccessible, navigateDesktop]);
  const createPostCapability = getNavigationStateForHref('/desktop/create-post');
  const canNavigateToCreatePost = !createPostCapability.managed || isAccessible(createPostCapability);

  useEffect(() => {
    let isCancelled = false;

    if (!hasDNA) {
      setHasAnyCampaigns(false);
      setIsCampaignStateReady(true);
      return undefined;
    }

    if (posts.length > 0) {
      setHasAnyCampaigns(true);
      setIsCampaignStateReady(true);
      return undefined;
    }

    setIsCampaignStateReady(false);

    const loadCampaignState = async () => {
      try {
        const campaigns = await campaignService.listCampaigns({ limit: 1 });
        if (isCancelled) return;
        setHasAnyCampaigns(Array.isArray(campaigns) && campaigns.length > 0);
      } catch (error) {
        if (isCancelled) return;
        logger.network('Failed to check campaigns for empty state', error);
        setHasAnyCampaigns(false);
      } finally {
        if (!isCancelled) {
          setIsCampaignStateReady(true);
        }
      }
    };

    loadCampaignState();

    return () => {
      isCancelled = true;
    };
  }, [hasDNA, posts.length]);


  const emptyStateContent = useMemo(() => {
    if (hasDNA && hasAnyCampaigns) {
      return {
        title: t('postReview.empty.firstPost.title', { defaultValue: 'Campaign ready. Create your first post.' }),
        description: t('postReview.empty.firstPost.description', {
          defaultValue: 'You already have a campaign but no posts yet. Open Create post to generate the first content.'
        }),
        features: [
          {
            icon: '✍️',
            label: t('postReview.empty.firstPost.features.creation', { defaultValue: 'Guided AI creation' })
          },
          {
            icon: '🎯',
            label: t('postReview.empty.firstPost.features.alignment', { defaultValue: 'Content aligned with your campaign' })
          },
          {
            icon: '📅',
            label: t('postReview.empty.firstPost.features.publish', { defaultValue: 'Ready to schedule and publish' })
          }
        ],
        primaryLabel: t('postReview.empty.firstPost.cta', { defaultValue: 'Create post' }),
        onPrimary: canNavigateToCreatePost ? handleNavigateToCreatePost : null
      };
    }

    if (hasDNA) {
      return {
        title: t('postReview.empty.campaign.title', { defaultValue: 'DNA completato! ✅' }),
        description: t('postReview.empty.campaign.description', {
          defaultValue: 'Ok, ora che hai il DNA crea la tua prima campagna social.'
        }),
        features: [
          {
            icon: '🎯',
            label: t('postReview.empty.campaign.features.strategy', { defaultValue: 'Campagne strategiche mirate' })
          },
          {
            icon: '🤖',
            label: t('postReview.empty.campaign.features.authenticAi', { defaultValue: 'Contenuti AI autentici' })
          },
          {
            icon: '📅',
            label: t('postReview.empty.campaign.features.scheduling', { defaultValue: 'Programmazione intelligente' })
          }
        ],
        primaryLabel: t('postReview.empty.campaign.cta', { defaultValue: 'Crea la tua prima campagna' }),
        onPrimary: handleNavigateToStrategy
      };
    }
    return {
      title: t('postReview.empty.dna.title', { defaultValue: 'Benvenuto su LOOPINO!' }),
      description: t('postReview.empty.dna.description', {
        defaultValue: 'Prima di iniziare, completa il DNA aziendale per permettere all\'AI di creare contenuti personalizzati e autentici.'
      }),
      features: [
        {
          icon: '🧬',
          label: t('postReview.empty.dna.features.identity', { defaultValue: 'DNA aziendale intelligente' })
        },
        {
          icon: '🤖',
          label: t('postReview.empty.dna.features.personalAi', { defaultValue: 'AI personalizzata sui tuoi valori' })
        },
        {
          icon: '✨',
          label: t('postReview.empty.dna.features.authenticContent', { defaultValue: 'Contenuti autentici e coerenti' })
        }
      ],
      primaryLabel: t('postReview.empty.dna.cta', { defaultValue: 'Completa il DNA Aziendale' }),
      onPrimary: handleNavigateToDna
    };
  }, [canNavigateToCreatePost, hasAnyCampaigns, hasDNA, handleNavigateToCreatePost, handleNavigateToDna, handleNavigateToStrategy, t]);

  useEffect(() => {
    updateEditorModalOffsets();
    window.addEventListener('resize', updateEditorModalOffsets);
    window.addEventListener('scroll', updateEditorModalOffsets, true);
    return () => {
      window.removeEventListener('resize', updateEditorModalOffsets);
      window.removeEventListener('scroll', updateEditorModalOffsets, true);
    };
  }, [updateEditorModalOffsets]);

  useEffect(() => {
    updateEditorModalOffsets();
  }, [currentPostIndex, posts.length, updateEditorModalOffsets]);

  const handlePrevPost = useCallback(() => {
    if (posts.length === 0) return;
    setCurrentPostIndex(prev => (prev - 1 + posts.length) % posts.length);
    setCurrentImageIndex(0);
  }, [posts.length]);

  const handleSelectPost = useCallback((index) => {
    if (index < 0 || index >= posts.length) return;
    setCurrentPostIndex(index);
    setCurrentImageIndex(0);
  }, [posts.length]);

  const handleOpenPostInCalendar = useCallback((postParam) => {
    const targetPost = postParam || currentPost;
    const postId = getPostIdentifier(targetPost);
    if (!postId && typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams();
    if (postId) {
      params.set('postId', postId);
    }

    const scheduledTimestamp = getPostScheduledTimestamp(targetPost);
    if (scheduledTimestamp) {
      try {
        params.set('scheduledAt', new Date(scheduledTimestamp).toISOString());
      } catch (error) {
        // ignore invalid date
      }
    }

    const href = `/desktop/calendar${params.toString() ? `?${params.toString()}` : ''}`;
    if (typeof onNavigate === 'function') {
      onNavigate(href);
      return;
    }

    if (typeof window !== 'undefined') {
      window.history.pushState({}, '', href);
      window.dispatchEvent(new Event('popstate'));
    }
  }, [currentPost, onNavigate]);

  const handleOpenCampaignPage = useCallback((postParam) => {
    const targetPost = postParam || currentPost;
    const campaignId = getCampaignIdentifier(targetPost);

    if (!campaignId) {
      if (notify) {
        notify({
          variant: 'info',
          title: t('postReview.desktop.noCampaign', { defaultValue: 'Nessuna campagna associata al post.' })
        });
      }
      return;
    }

    const href = `/desktop/strategy?campaignId=${encodeURIComponent(campaignId)}`;

    if (typeof onNavigate === 'function') {
      onNavigate(href, { state: { targetCampaignId: campaignId } });
      return;
    }

    if (typeof window !== 'undefined') {
      window.history.pushState({ targetCampaignId: campaignId }, '', href);
      window.dispatchEvent(new Event('popstate'));
    }
  }, [currentPost, notify, onNavigate, t]);

  const handleNextPost = useCallback(() => {
    if (posts.length === 0) return;
    setCurrentPostIndex(prev => (prev + 1) % posts.length);
    setCurrentImageIndex(0);
  }, [posts.length]);

  const handlePostApprove = useCallback(async (post) => {
    if (isRegenerating) return;
    if (isPostPublished(post)) return;
    if (!post || !apiApprovePost) return;
    setIsProcessing(true);
    try {
      await apiApprovePost(post);
      await apiRefresh();
    } catch (error) {
      logger.network('Error approving post', error, { postId });
    } finally {
      setIsProcessing(false);
    }
  }, [apiApprovePost, apiRefresh, isRegenerating]);

  const handlePostReject = useCallback(async (post) => {
    if (isRegenerating) return;
    if (isPostPublished(post)) return;
    if (!post || !apiRejectPost) return;
    setIsProcessing(true);
    try {
      await apiRejectPost(post);
      await apiRefresh();
    } catch (error) {
      logger.network('Error rejecting post', error, { postId });
    } finally {
      setIsProcessing(false);
    }
  }, [apiRejectPost, apiRefresh, isRegenerating]);

  const handlePostDelete = useCallback(async (post) => {
    if (isRegenerating) return;
    if (isPostPublished(post)) return;
    if (!post || !apiDeletePost) return;
    setIsProcessing(true);
    try {
      await apiDeletePost(post);
      await apiRefresh();
    } catch (error) {
      logger.network('Error deleting post', error, { postId });
    } finally {
      setIsProcessing(false);
    }
  }, [apiDeletePost, apiRefresh, isRegenerating]);

  const openAIModal = useCallback((type = 'full', imageIndex = null) => {
    if (isEditorLocked) return;
    setAIModalType(type || 'full');
    setAiModalImageIndex(imageIndex);
    setAIModalOpen(true);
  }, [isEditorLocked]);

  const closeAIModal = useCallback(() => {
    setAIModalOpen(false);
    setAiModalImageIndex(null);
  }, []);

  const handlePostRegenerate = useCallback((post, type) => {
    if (isEditorLocked) return;
    if (!post) return;
    openAIModal(type || 'full');
  }, [isEditorLocked, openAIModal]);

  const handleAIRegeneration = useCallback(async ({ type, prompt, imageIndex, postOverride }) => {
    setIsAiRegeneratingUi(true);
    // Make sure the modal state flips immediately even if the caller forgets to close it
    setAIModalOpen(false);
    setAiModalImageIndex(null);

    if (isEditorLocked) {
      setIsAiRegeneratingUi(false);
      return;
    }
    const targetPost = postOverride || currentPost;
    if (!targetPost || !apiRegeneratePost) {
      setIsAiRegeneratingUi(false);
      return;
    }

    try {
      const payload = {};
      if (prompt) {
        payload.user_prompt = prompt;
      }
      if (typeof imageIndex === 'number') {
        payload.imageIndex = imageIndex;
      }

      await apiRegeneratePost(targetPost, type || 'full', payload);
      await apiRefresh();
      closeAIModal();
    } catch (error) {
      logger.provider('Error with AI regeneration', error, { postId });
    } finally {
      setIsAiRegeneratingUi(false);
    }
  }, [apiRefresh, apiRegeneratePost, closeAIModal, currentPost, isEditorLocked]);

  const handleOpenPromptEditor = useCallback((imageIndex = 0, targetPost = null) => {
    if (isEditorLocked) return;
    const resolvedPost = targetPost || currentPost;
    if (!resolvedPost) return;
    const postId = getPostIdentifier(resolvedPost) || 'unknown';
    const targetImageIndex = typeof imageIndex === 'number' ? imageIndex : 0;

    if (typeof onNavigate === 'function') {
      const url = `/desktop/image-generator?postId=${encodeURIComponent(postId)}&imageIndex=${targetImageIndex}`;
      onNavigate(url, {
        state: {
          promptEditorPost: resolvedPost,
          promptEditorImageIndex: targetImageIndex
        }
      });
    }
  }, [currentPost, isEditorLocked, onNavigate]);

  const openTextEditModal = useCallback(() => {
    if (isEditorLocked) return;
    setTextEditModalOpen(true);
  }, [isEditorLocked]);

  const closeTextEditModal = useCallback(() => {
    setTextEditModalOpen(false);
  }, []);

  const handleTextSave = useCallback(async (newText) => {
    if (isEditorLocked) {
      return;
    }
    if (!currentPost || !apiEditPostText) {
      return;
    }

    setIsTextSaving(true);
    try {
      await apiEditPostText(currentPost, newText);
      await apiRefresh();
      closeTextEditModal();
    } catch (error) {
      logger.network('Error saving text', error, { postId });
      if (apiClearError) apiClearError();
    } finally {
      setIsTextSaving(false);
    }
  }, [apiEditPostText, apiRefresh, closeTextEditModal, currentPost, apiClearError, isEditorLocked]);

  const handleImageUpload = useCallback((imageIndex) => {
    if (isEditorLocked) {
      return;
    }
    if (!currentPost || !apiHandleImageUpload) {
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = SUPPORTED_MEDIA_ACCEPT;
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!isSupportedMediaFile(file)) {
        notify({
          variant: 'error',
          title: unsupportedFormatMessage
        });
        return;
      }

      try {
        await apiHandleImageUpload(currentPost, imageIndex, file);
        await apiRefresh();
      } catch (error) {
        logger.network('Error uploading image', error, { postId });
        if (apiClearError) apiClearError();
      }
    };
    input.click();
  }, [apiHandleImageUpload, apiRefresh, currentPost, notify, unsupportedFormatMessage, apiClearError, isEditorLocked]);

  const handleVersionSelect = useCallback((post, versionIndex) => {
    if (isRegenerating) {
      return;
    }
    if (isPostPublished(post)) {
      return;
    }
    const postId = getPostIdentifier(post);
    if (!postId || !postVersions[postId]) {
      return;
    }

    const versions = postVersions[postId];
    if (versionIndex < 0 || versionIndex >= versions.length) {
      return;
    }

    const selectedVersion = versions[versionIndex];

    setCurrentVersionIndices(prev => ({
      ...prev,
      [postId]: versionIndex
    }));

    setPosts(prevPosts => prevPosts.map(p => {
      if (getPostIdentifier(p) === postId) {
        return {
          ...p,
          content: selectedVersion.content,
          description: selectedVersion.content,
          hashtags: selectedVersion.hashtags || p.hashtags,
          media_urls: selectedVersion.media_urls || p.media_urls,
          images: selectedVersion.images || p.images
        };
      }
      return p;
    }));
  }, [isRegenerating, postVersions]);

  const handleSetDefinitiveVersion = useCallback(async (post) => {
    if (isRegenerating) {
      return;
    }
    if (isPostPublished(post)) {
      return;
    }
    const postId = getPostIdentifier(post);
    if (!postId) {
      return;
    }

    const currentIndex = currentVersionIndices[postId];
    if (currentIndex === undefined || currentIndex === null) {
      return;
    }

    const versions = postVersions[postId];
    if (!versions || !versions[currentIndex]) {
      return;
    }

    const selectedVersion = versions[currentIndex];
    const versionId = selectedVersion.version_id;
    if (!versionId) {
      return;
    }

    setIsSettingDefinitiveVersion(true);

    try {
      const url = `${API_CONFIG.BASE_URL}/api/v1/posts/${postId}/versions/${versionId}/activate`;
      const response = await apiFetch(url, {
        method: 'POST'
      });

      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent('auth-expired'));
        if (authLogout) {
          await authLogout();
        }
        throw new Error('Failed to activate version: 401');
      }

      if (!response.ok) {
        throw new Error(`Failed to activate version: ${response.status}`);
      }

      const data = await parseResponseBody(response);

      setPosts(prevPosts => prevPosts.map(p => {
        if (getPostIdentifier(p) === postId) {
          const backendPost = data?.post || {};
          const merged = {
            ...p,
            ...backendPost,
            id: backendPost.id || backendPost._id || p.id,
            current_version_id: backendPost.current_version_id || versionId
          };

          return {
            ...merged,
            content: backendPost.content ?? selectedVersion.content ?? merged.content,
            description: backendPost.content ?? selectedVersion.content ?? merged.description,
            hashtags: backendPost.hashtags ?? selectedVersion.hashtags ?? merged.hashtags,
            media_urls: backendPost.media_urls ?? selectedVersion.media_urls ?? merged.media_urls,
            images: backendPost.images ?? selectedVersion.images ?? merged.images
          };
        }
        return p;
      }));

      setDefinitiveVersionIndices(prev => ({
        ...prev,
        [postId]: currentIndex
      }));
    } catch (error) {
      logger.network('Error setting definitive version', error, { postId, versionId });
    } finally {
      setIsSettingDefinitiveVersion(false);
    }
  }, [authLogout, currentVersionIndices, isRegenerating, postVersions]);

  const handleEditorSave = useCallback(async (updatedPost) => {
    if (isEditorLocked) return;
    if (updatedPost) {
      setPosts(prev => prev.map(p => {
        if (getPostIdentifier(p) === getPostIdentifier(updatedPost)) {
          return {
            ...p,
            ...updatedPost
          };
        }
        return p;
      }));
    }
    await apiRefresh();
  }, [apiRefresh, isEditorLocked]);

  const closeDesktopDatePicker = useCallback(() => {
    setDesktopDatePickerOpen(false);
    setDesktopTimePickerOpen(false);
  }, []);

  const openEditorDatePicker = useCallback(() => {
    if (isEditorLocked) return;
    syncDatePickerWithPost();
    setDesktopTimePickerOpen(false);
    setDesktopDatePickerOpen(true);
  }, [isEditorLocked, syncDatePickerWithPost]);

  useEffect(() => {
    if (isEditorLocked) {
      setDesktopGalleryOpen(false);
      setAIModalOpen(false);
      setTextEditModalOpen(false);
      setDesktopDatePickerOpen(false);
      setDesktopTimePickerOpen(false);
    }
  }, [isEditorLocked]);

  const versionsForCurrentPost = useMemo(() => {
    if (!currentPostId) {
      return null;
    }
    return postVersions[currentPostId] || null;
  }, [currentPostId, postVersions]);

  const hasVersions = useMemo(() => {
    return Array.isArray(versionsForCurrentPost) && versionsForCurrentPost.length > 0;
  }, [versionsForCurrentPost]);

  const currentVersionIndex = useMemo(() => {
    if (!hasVersions || !versionsForCurrentPost) {
      return 0;
    }
    const candidate = currentPostId ? currentVersionIndices[currentPostId] : 0;
    return typeof candidate === 'number' && candidate >= 0 && candidate < versionsForCurrentPost.length
      ? candidate
      : 0;
  }, [currentPostId, currentVersionIndices, hasVersions, versionsForCurrentPost]);

  const definitiveVersionIndex = useMemo(() => {
    if (!hasVersions || !currentPostId) {
      return undefined;
    }
    const candidate = definitiveVersionIndices[currentPostId];
    return typeof candidate === 'number' ? candidate : undefined;
  }, [currentPostId, definitiveVersionIndices, hasVersions]);

  const renderPreviewColumn = () => {
    const previewClassName = [
      'loopino-desktop-post-preview-sticky',
      previewPinMode === 'fixed' ? 'is-fixed' : '',
      previewPinMode === 'bottom' ? 'is-bottom' : ''
    ].filter(Boolean).join(' ');

    const mergedPreviewStyle = {
      ...(previewPanelStyle || {}),
      ...(previewDynamicStyle || {})
    };

    if (isUiLoading || isCheckingCampaignState) {
      return (
        <>
          <div className="loopino-desktop-post-preview-placeholder" ref={previewPlaceholderRef} aria-hidden />
          <div
            className={previewClassName}
            style={mergedPreviewStyle}
            ref={previewPanelRef}
          >
            <div className="loopino-desktop-post-preview-panel loopino-desktop-post-preview-flat" aria-busy="true">
              <div className="loopino-desktop-post-device">
                <div className="loopino-desktop-post-device-frame">
                  <Iphone16Pro
                    width={410}
                    height={840}
                    deviceTime={deviceTime}
                    shadow
                    hoverAnimation={false}
                    isSkeleton
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      );
    }

    if (!currentPost) {
      return (
        <>
          <div className="loopino-desktop-post-preview-placeholder" ref={previewPlaceholderRef} aria-hidden />
          <div
            className={previewClassName}
            style={mergedPreviewStyle}
            ref={previewPanelRef}
          >
            <div className="loopino-desktop-post-preview-panel loopino-desktop-post-placeholder">
              <p>{t('postReview.desktop.noPosts', { defaultValue: 'Nessun post disponibile. Genera contenuti dalla versione mobile per iniziare.' })}</p>
              <button type="button" onClick={() => apiRefresh()}>
                {t('common.actions.refresh', { defaultValue: 'Aggiorna' })}
              </button>
            </div>
          </div>
        </>
      );
    }

    const mediaUrls = getAllMediaUrls(currentPost);
    const profileName = companyProfileName || resolveCampaignNameForPost(currentPost) || currentPost?.platform || 'loopino.ai';
    const profileLocation = currentPost?.location || t('postReview.preview.location.featured', { defaultValue: 'In evidenza' });
    const captionText = currentPost?.content || currentPost?.description || t('postReview.details.emptyDescription');
    const hashtagsLine = normalizeHashtagsList(currentPost?.hashtags).join(' ');
    const scheduledTimestamp = getPostScheduledTimestamp(currentPost);
    const relativeTimeLabel = formatPostRelativeTimeLabel(scheduledTimestamp, t);
    const avatarUrl = companyAvatarUrl;
    const likeCount = Math.max(124, (mediaUrls?.length || 1) * 182);
    const commentsHint = t('postReview.preview.commentsHint', { defaultValue: 'Visualizza tutti i commenti' });

    return (
      <>
        <div className="loopino-desktop-post-preview-placeholder" ref={previewPlaceholderRef} aria-hidden />
        <div
          className={previewClassName}
          style={mergedPreviewStyle}
          ref={previewPanelRef}
        >
          <div className="loopino-desktop-post-preview-panel loopino-desktop-post-preview-flat">
            <div className="loopino-desktop-post-device">
              <div className="loopino-desktop-post-device-frame">
                <Iphone16Pro
                  width={410}
                  height={840}
                  deviceTime={deviceTime}
                  profileName={profileName}
                  profileLocation={profileLocation}
                  relativeTimeLabel={relativeTimeLabel}
                  avatarUrl={avatarUrl}
                  mediaUrls={mediaUrls}
                  currentImageIndex={currentImageIndex}
                  onImageIndexChange={setCurrentImageIndex}
                  onRegenerateImage={(index) => openAIModal('image', index)}
                  onUploadImage={handleImageUpload}
                  isRegenerating={isRegenerating}
                  isProcessing={isProcessing}
                  isCurrentPostPublished={isCurrentPostPublished}
                  getImageLoadingProps={getImageLoadingProps}
                  likeCount={likeCount}
                  captionText={captionText}
                  hashtagsLine={hashtagsLine}
                  commentsHint={commentsHint}
                />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderEditorColumn = () => {
    const desktopEditorTitle = t('postReview.desktop.editorTitle', { defaultValue: 'Modifica post' });

    if (isUiLoading && !currentPost) {
      return (
        <div className="loopino-desktop-post-editor-panel loopino-desktop-post-editor-panel-loading" aria-busy="true">
          <DesktopPostEditPortal
            anchorKey="loading"
            headerTitle={desktopEditorTitle}
            onRequestGallery={null}
            headerContent={(
              <div className="loopino-desktop-post-info-section" aria-hidden="true">
                <DesktopPostInfo isLoading />
              </div>
            )}
            footerContent={(
              <div className="loopino-desktop-post-versions-section" aria-hidden="true">
                <div className="loopino-desktop-post-versions-skeleton">
                  <div className="loopino-desktop-post-versions-skeleton-header">
                    <Skeleton variant="rounded" width="42%" height={16} shimmer />
                    <Skeleton variant="rounded" width="28%" height={12} shimmer />
                  </div>
                  <div className="loopino-desktop-post-versions-skeleton-thumbs">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <div className="loopino-desktop-post-versions-skeleton-thumb" key={`versions-skel-thumb-${index}`}>
                        <Skeleton variant="rounded" width="100%" height="100%" shimmer />
                      </div>
                    ))}
                  </div>
                  <Skeleton variant="rounded" width="100%" height={44} shimmer />
                </div>
              </div>
            )}
          >
            <DesktopPostEditPage
              key="loading"
              post={null}
              onClose={() => {}}
              onSave={() => {}}
              onAIRegenerate={() => {}}
              onOpenPromptEditor={() => {}}
              onAddImage={null}
              onClearError={null}
              isReadOnly
              isLoading
            />
          </DesktopPostEditPortal>
        </div>
      );
    }

    if (!currentPost) {
      return (
        <div className="loopino-desktop-post-editor-panel loopino-desktop-post-placeholder">
          <p>{t('postReview.desktop.waitSelection', { defaultValue: 'Seleziona un post dalla colonna di sinistra per iniziare a modificarlo.' })}</p>
        </div>
      );
    }

    return (
      <div
        className="loopino-desktop-post-editor-panel"
        id="loopino-desktop-post-editor"
        ref={editorPanelRef}
        style={{
          '--loopino-desktop-editor-offset-top': `${editorModalOffsets.top}px`,
          '--loopino-desktop-editor-offset-left': `${editorModalOffsets.left}px`,
          '--loopino-desktop-editor-offset-right': `${editorModalOffsets.right}px`,
          '--loopino-desktop-editor-offset-bottom': `${editorModalOffsets.bottom}px`
        }}
      >
        <div className="loopino-desktop-post-editor-wrapper">
          <DesktopPostEditPortal
            anchorKey={currentPostId || 'empty'}
            headerTitle={desktopEditorTitle}
            onRequestGallery={handleOpenDesktopGallery}
            headerContent={(
              <div className="loopino-desktop-post-info-section">
                <DesktopPostInfo
                  post={currentPost}
                  currentIndex={currentPostIndex}
                  totalPosts={posts.length}
                  campaignName={resolveCampaignNameForPost(currentPost)}
                  thumbnailImage={getFirstMediaUrl(currentPost)}
                  allPosts={posts}
                  onApprove={isEditorLocked ? null : handlePostApprove}
                  onReject={isEditorLocked ? null : handlePostReject}
                  onDeletePost={isEditorLocked ? null : handlePostDelete}
                  onAIRegenerate={isEditorLocked ? null : handlePostRegenerate}
                  onCampaignClick={handleOpenCampaignPage}
                  onThumbnailClick={() => handleOpenPostInCalendar(currentPost)}
                  onOpenDatePicker={isEditorLocked ? null : openEditorDatePicker}
                  isApproved={currentPost?.status === 'scheduled'}
                  isProcessing={isProcessing}
                  isRegenerating={isRegenerating}
                />
              </div>
            )}
            footerContent={hasVersions ? (
              <div className="loopino-desktop-post-versions-section">
                  <DesktopPostVersions
                    versions={versionsForCurrentPost}
                    currentVersionIndex={currentVersionIndex}
                    definitiveVersionIndex={definitiveVersionIndex}
                    onVersionSelect={!isEditorLocked ? (index) => handleVersionSelect(currentPost, index) : undefined}
                    onSetDefinitiveVersion={!isEditorLocked ? () => handleSetDefinitiveVersion(currentPost) : undefined}
                    isSettingDefinitive={isSettingDefinitiveVersion || isEditorLocked}
                    parentPost={currentPost}
                    getImageLoadingProps={getImageLoadingProps}
                    imageLoadingScope="preview-versions"
                  />
              </div>
            ) : null}
          >
            <DesktopPostEditPage
              key={currentPostId || 'empty'}
              post={currentPost}
              onClose={() => {}}
              onSave={handleEditorSave}
              onAIRegenerate={handlePostRegenerate}
              onOpenPromptEditor={handleOpenPromptEditor}
              onAddImage={null}
              onClearError={apiClearError}
              isReadOnly={isEditorLocked}
            />
          </DesktopPostEditPortal>
        </div>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className="loopino-desktop-post-empty">
      <section className="desktop-login-hero loopino-desktop-post-empty-hero" aria-live="polite">
        <div className="desktop-hero-glow" aria-hidden="true" />
        <div className="desktop-login-hero-card">
          <DesktopThreadsBackground
            color={heroThreadsColor}
            amplitude={1}
            distance={0.3}
            enableMouseInteraction
            aria-hidden="true"
          />
          <div className="desktop-hero-content loopino-desktop-post-empty-content">
            <div className="loopino-desktop-post-empty-copy">
              <h2>{emptyStateContent.title}</h2>
              <p>{emptyStateContent.description}</p>
            </div>
            <div className="loopino-desktop-post-empty-features">
              {emptyStateContent.features.map((feature, index) => (
                <div className="loopino-desktop-post-empty-feature" key={`${feature.label}-${index}`}>
                  <span className="loopino-desktop-post-empty-feature-icon">{feature.icon}</span>
                  <span className="loopino-desktop-post-empty-feature-text">{feature.label}</span>
                </div>
              ))}
            </div>
            <div className="loopino-desktop-post-empty-actions">
              <button
                type="button"
                className="loopino-button"
                data-variant="primary"
                onClick={emptyStateContent.onPrimary}
                disabled={!emptyStateContent.onPrimary}
              >
                {emptyStateContent.primaryLabel}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const renderPostRail = () => {
    const placeholderLabel = t('postReview.desktop.previewLabel', { defaultValue: 'Anteprima post' });
    const formatPostDate = (post) => {
      const timestamp = getPostScheduledTimestamp(post) ??
        (() => {
          const raw = post?.created_at || post?.createdAt || post?.timestamp;
          if (!raw) return null;
          const normalized = typeof raw === 'object' && raw.$date ? raw.$date : raw;
          const time = new Date(normalized).getTime();
          return Number.isFinite(time) ? time : null;
        })();

      if (!timestamp) {
        return t('postReview.desktop.previewFallbackDate', { defaultValue: 'Data non disponibile' });
      }

      try {
        return new Date(timestamp).toLocaleDateString('it-IT', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });
      } catch (error) {
        return t('postReview.desktop.previewFallbackDate', { defaultValue: 'Data non disponibile' });
      }
    };

    return (
      <div className="loopino-desktop-post-strip" aria-label={placeholderLabel}>
        <DesktopHorizontalScroll trackClassName="loopino-desktop-post-strip-track" padding="20px">
          {(isUiLoading || isCheckingCampaignState) && posts.length === 0 && (
            Array.from({ length: 4 }).map((_, index) => (
              <div className="loopino-desktop-post-chip is-loading" key={`loading-chip-${index}`}>
                <Skeleton variant="rounded" width="100%" height="100%" shimmer />
              </div>
            ))
          )}

          {!isUiLoading && !isCheckingCampaignState && posts.length === 0 && (
            <div className="loopino-desktop-post-strip-empty">
              {t('postReview.desktop.previewRailEmpty', { defaultValue: 'Nessun post disponibile da mostrare.' })}
            </div>
          )}

          {posts.map((post, index) => {
            const isActive = index === currentPostIndex;
            const thumbnail = getFirstMediaUrl(post);
            const postId = getPostIdentifier(post) || index;
            const blurProps = thumbnail ? getImageLoadingProps(thumbnail, `strip-${postId}`) : null;
            const showSkeleton = blurProps && blurProps.status !== 'loaded';
            return (
              <button
                type="button"
                key={`post-chip-${postId}`}
                className={`loopino-desktop-post-chip${isActive ? ' is-active' : ''}`}
                onClick={() => handleSelectPost(index)}
                aria-pressed={isActive}
                ref={(el) => { postChipRefs.current[index] = el; }}
              >
                <div className="loopino-desktop-post-chip-thumb">
                  {thumbnail ? (
                    <>
                      <img
                        src={thumbnail}
                        alt={placeholderLabel}
                        draggable={false}
                        className={[
                          blurProps ? 'loopino-desktop-image-with-loading' : '',
                          blurProps?.className || ''
                        ].filter(Boolean).join(' ')}
                        loading="lazy"
                        decoding="async"
                        onLoad={(event) => {
                          blurProps?.onLoad?.(event);
                        }}
                        onError={(event) => {
                          blurProps?.onError?.(event);
                          event.currentTarget.onerror = null;
                          event.currentTarget.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
                        }}
                      />
                      {showSkeleton && (
                        <div className="loopino-desktop-post-chip-skeleton" aria-hidden>
                          <Skeleton variant="rounded" width="100%" height="100%" shimmer />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="loopino-desktop-post-chip-placeholder" aria-hidden>
                      {/* empty placeholder */}
                    </div>
                  )}
                </div>
                <div className="loopino-desktop-post-chip-meta">
                  <span className="loopino-desktop-post-chip-date">{formatPostDate(post)}</span>
                </div>
              </button>
            );
          })}
        </DesktopHorizontalScroll>
      </div>
    );
  };

  if (isDesktopGalleryOpen && !isEditorLocked) {
    return (
      <DesktopImageGalleryPage
        onClose={handleCloseDesktopGallery}
        onSelect={handleDesktopGallerySelect}
        viewerOrigin="desktop-post-review"
      />
    );
  }

  return (
    <>
      {isBlockingModalActive && typeof document !== 'undefined' ? createPortal(
        <div className="loopino-desktop-modal-scrim" aria-hidden="true" />,
        document.body
      ) : null}

      <div
        className={`loopino-desktop-shell loopino-desktop-post-review-shell${isEmptyStateReady ? ' is-empty' : ''}`}
      >
        {isEmptyStateReady ? (
          renderEmptyState()
        ) : (
          <>
            <header className="loopino-desktop-post-review-header" ref={pageHeaderRef}>
              <div>
                <p className="loopino-desktop-post-review-eyebrow">
                  {t('desktop.common.eyebrow', { defaultValue: 'Loopino desktop' })}
                </p>
                <h1>{t('postReview.desktop.title', { defaultValue: 'Revisione post' })}</h1>
                <p className="loopino-desktop-post-review-summary">{pageSummary}</p>
              </div>
              <div className="loopino-desktop-post-review-actions">
                <button
                  type="button"
                  className="loopino-desktop-post-nav"
                  onClick={handlePrevPost}
                  disabled={posts.length === 0}
                  aria-label={t('postReview.desktop.actions.prev', { defaultValue: 'Post precedente' })}
                >
                  &lt;
                </button>
                <button
                  type="button"
                  className="loopino-desktop-post-nav"
                  onClick={handleNextPost}
                  disabled={posts.length === 0}
                  aria-label={t('postReview.desktop.actions.next', { defaultValue: 'Post successivo' })}
                >
                  &gt;
                </button>
                <button
                  type="button"
                  className="loopino-desktop-post-refresh"
                  onClick={() => apiRefresh()}
                >
                  {t('common.actions.refresh', { defaultValue: 'Aggiorna' })}
                </button>
              </div>
            </header>

            {renderPostRail()}

            {apiError && (
              <div className="loopino-desktop-post-review-error">
                <p>{apiError}</p>
                <button type="button" onClick={() => apiRefresh()}>
                  {t('common.actions.retry', { defaultValue: 'Riprova' })}
                </button>
              </div>
            )}

            <div className="loopino-desktop-post-review-grid" ref={postReviewGridRef}>
              {renderPreviewColumn()}
              {renderEditorColumn()}
            </div>
          </>
        )}
      </div>

      {isDesktopDatePickerOpen && !isEditorLocked ? (
        <div
          className="mobile-date-picker-overlay"
          onClick={closeDesktopDatePicker}
          aria-hidden="true"
        >
          <div
            className="mobile-date-picker-popover"
            id="desktop-date-picker-popover"
            ref={desktopDatePickerRef}
            role="dialog"
            aria-label={t('postReview.postEdit.time.label', { defaultValue: 'Data e ora' })}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mobile-date-picker comp-503">
              <div className="mobile-datetime-label">
                <CalendarIcon size={18} />
                <span>{t('postReview.postEdit.time.label')}</span>
              </div>
              <div className="mobile-date-picker-header">
                <button
                  type="button"
                  className="date-picker-nav"
                  onClick={goToPreviousDesktopMonth}
                  aria-label={t('postReview.postEdit.time.prevMonth', { defaultValue: 'Mese precedente' })}
                >
                  <BackIcon size={18} />
                </button>
                <div className="mobile-date-picker-month">{desktopMonthLabel}</div>
                <button
                  type="button"
                  className="date-picker-nav next"
                  onClick={goToNextDesktopMonth}
                  aria-label={t('postReview.postEdit.time.nextMonth', { defaultValue: 'Mese successivo' })}
                >
                  <BackIcon size={18} />
                </button>
              </div>

              <div className="mobile-date-picker-body single-column">
                <div className="mobile-date-picker-calendar">
                  <div className="mobile-date-picker-weekdays">
                    {desktopWeekdayLabels.map((label) => (
                      <span key={label} className="mobile-date-picker-weekday">
                        {label}
                      </span>
                    ))}
                  </div>
                  <div className="mobile-date-picker-grid">
                    {desktopCalendarDays.map((day, index) => {
                      if (!day) {
                        return <span key={`empty-${index}`} className="mobile-date-picker-day empty"></span>;
                      }
                      const isSelected = isSameDay(day, desktopSelectedDate);
                      const isToday = isSameDay(day, desktopToday);
                      const isDisabled = day < desktopToday;
                      return (
                        <button
                          type="button"
                          key={day.getTime()}
                          className={`mobile-date-picker-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isDisabled ? 'disabled' : ''}`}
                          onClick={() => handleDesktopDateSelect(day)}
                          disabled={isDisabled}
                        >
                          <span>{day.getDate()}</span>
                          {isToday && <span className="mobile-date-picker-dot"></span>}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mobile-date-picker-time-input">
                  <div className="time-input-label-row">
                    <ClockIcon size={16} />
                    <span>{t('postReview.postEdit.time.chooseHour', { defaultValue: 'Scegli orario' })}</span>
                  </div>
                  <div
                    className="time-input-wrapper"
                    onClick={openDesktopTimePicker}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openDesktopTimePicker();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <input
                      type="time"
                      step="60"
                      value={desktopSelectedTime || '12:00'}
                      onChange={(e) => handleDesktopTimeInputChange(e.target.value)}
                      className="time-input-field"
                      ref={desktopTimeInputRef}
                    />
                    <div className="time-input-icon">
                      <ClockIcon size={16} aria-hidden="true" />
                    </div>

                    {isDesktopTimePickerOpen && (
                      <div className="time-picker-panel" ref={desktopTimePanelRef}>
                        <div className="time-picker-columns">
                          <div className="time-picker-column">
                            <div className="time-picker-column-label">
                              {t('postReview.postEdit.time.hours', { defaultValue: 'Ore' })}
                            </div>
                            <div className="time-picker-scroll">
                              {desktopHourOptions.map((hour) => {
                                const isSelected = (desktopSelectedTime || '12:00').startsWith(hour);
                                return (
                                  <button
                                    type="button"
                                    key={hour}
                                    className={`time-picker-option ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleDesktopHourSelect(hour)}
                                  >
                                    {hour}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <div className="time-picker-column">
                            <div className="time-picker-column-label">
                              {t('postReview.postEdit.time.minutes', { defaultValue: 'Minuti' })}
                            </div>
                            <div className="time-picker-scroll">
                              {desktopMinuteOptions.map((minute) => {
                                const isSelected = (desktopSelectedTime || '12:00').split(':')[1] === minute;
                                return (
                                  <button
                                    type="button"
                                    key={minute}
                                    className={`time-picker-option ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleDesktopMinuteSelect(minute)}
                                  >
                                    {minute}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {typeof document !== 'undefined' && aiModalOpen && !isEditorLocked ? createPortal(
        <AIRegenerationModal
          isOpen={aiModalOpen}
          onClose={closeAIModal}
          type={aiModalType}
          post={currentPost}
          imageIndex={aiModalImageIndex}
          onRegenerate={handleAIRegeneration}
          isRegenerating={isRegenerating}
        />,
        document.body
      ) : null}

      {typeof document !== 'undefined' && textEditModalOpen && !isEditorLocked ? createPortal(
        <TextEditModal
          isOpen={textEditModalOpen}
          onClose={closeTextEditModal}
          text={currentPost?.content || currentPost?.description || ''}
          onSave={handleTextSave}
          isSaving={isTextSaving}
        />,
        document.body
      ) : null}
    </>
  );
};

const DesktopPostReviewPage = ({ onNavigate = null, onReady = null }) => (
  <DesktopAlertProvider>
    <DesktopPostReviewPageInner onNavigate={onNavigate} onReady={onReady} />
  </DesktopAlertProvider>
);

export default DesktopPostReviewPage;
